import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  CircularProgress,
  Divider,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useSmartAuth } from '../hooks/useSmartAuth';
import MyDevice from '../Components/MyDevice';
import AuthHelpWizard from '../Components/AuthHelpWizard';

export default function AuthPage() {
  const {
    username,
    setUsername,
    state,
    view,
    context,
    buttonState,
    lookup,
    runAction,
  } = useSmartAuth();
  const [searchParams] = useSearchParams();
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setUsername(emailParam);
    }
  }, [searchParams, setUsername]);

  const renderMessage = () => {
    if (!view?.message) return null;
    return (
      <Alert severity={view.message.severity} sx={{ mb: 2 }}>
        {view.message.segments.map((s, i) =>
          s.device ? (
            <MyDevice key={i} device={context?.device} />
          ) : (
            <span key={i}>{s.text}</span>
          ),
        )}
        {view.showSyncTip && (
          <Typography
            variant="caption"
            sx={{ display: 'block', mt: 1, color: 'text.secondary' }}
          >
            If you use Google Password Manager, try Sign in first — your passkey
            may already be here.
          </Typography>
        )}
        {view.showHybridNote && (
          <Typography
            variant="caption"
            sx={{ display: 'block', mt: 1, color: 'text.secondary' }}
          >
            "Use my phone" opens your browser's own pop-up to sign in with the
            passkey on your phone — look for "use a phone or tablet". It needs
            Bluetooth on both devices and must be run by your browser, not this
            app, so it may look different or not appear on every device.
          </Typography>
        )}
      </Alert>
    );
  };

  return (
    <Card variant="outlined">
      <CardHeader title="Bookmarks Login" subheader="Passwordless sign-in" />
      <CardContent>
        <TextField
          label="Email"
          variant="outlined"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          onBlur={() => {
            // Blur/Enter only ADVANCE the state machine: they trigger a lookup
            // ONLY when the button is still in its unresolved 'lookup' state.
            // Once resolved, the button's kind is the action (signIn/etc), so
            // blur can't re-trigger a lookup — no dual-trigger desync.
            if (username.trim() && buttonState.kind === 'lookup')
              runAction('lookup');
          }}
          onKeyDown={(e) => {
            if (
              e.key === 'Enter' &&
              username.trim() &&
              buttonState.kind === 'lookup'
            )
              runAction('lookup');
          }}
          fullWidth
          autoComplete="username"
          name="username"
          id="username"
        />

        <Box sx={{ mt: 2 }}>{renderMessage()}</Box>
      </CardContent>

      <Divider />

      <CardActions>
        <Stack sx={{ width: '100%' }} spacing={1}>
          <Button
            variant="contained"
            fullWidth
            disabled={buttonState.disabled}
            // The button is self-describing: data-kind is the action a click
            // runs; data-authstate is the resolved auth state (for CSS/tests).
            data-kind={buttonState.kind ?? ''}
            data-authstate={state ?? ''}
            startIcon={
              buttonState.busy ? (
                <CircularProgress size={18} color="inherit" />
              ) : undefined
            }
            onClick={() => {
              if (!buttonState.disabled && buttonState.kind)
                runAction(buttonState.kind);
            }}
          >
            {buttonState.label}
          </Button>

          {buttonState.secondary && (
            <Link
              component="button"
              type="button"
              variant="body2"
              underline="hover"
              sx={{ alignSelf: 'center' }}
              onClick={() => runAction(buttonState.secondary!.kind)}
            >
              {buttonState.secondary.label}
            </Link>
          )}

          <Stack
            direction="row"
            spacing={2}
            sx={{
              width: '100%',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Link
              component="button"
              type="button"
              variant="caption"
              underline="hover"
              onClick={() => setHelpOpen(true)}
            >
              Help me sign in
            </Link>
            <Typography variant="caption" color="text.secondary">
              v{process.env.REACT_APP_VERSION || '0.2.0'}
            </Typography>
          </Stack>
        </Stack>
      </CardActions>

      <AuthHelpWizard
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        state={state}
      />
    </Card>
  );
}
